import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { Card, Button, EmptyState, StatusRing } from "../shared";
import { fullName, formatDate, formatMonthDay, upcomingBirthdays, parseDateOnly } from "../../lib/format";
import { DepartmentWidget, apiListDepartments } from "../departments";
import { useAppSettings } from "../../lib/AppSettingsContext";
import { apiAdminUpdateUser } from "../admin/api";
import { buildItemsForRange, ymdKey } from "../calendar/helpers";
import { apiCreateTask } from "../tasks/api";

const WIDGET_REGISTRY = [
  { id: "birthdays", title: "Upcoming Birthdays", customIcon: "birthday-cake", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "Birthdays in the next 7 days.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "my_attendance", title: "My Attendance", customIcon: "clipboard", iconBg: "rgba(96,165,250,0.18)",
    description: "Your most recent service attendance.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "upcoming_events", title: "Upcoming Events", customIcon: "event-ticket", iconBg: "rgba(232,200,74,0.18)",
    description: "The next 2 upcoming events with dates.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "my_week", title: "My Week at a Glance", customIcon: "calendar-clock", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "Next 7 days of services, events, appointments & birthdays — honours your Calendar scope toggle. Tap a day for the full Day view, or + to send any item to your tasks.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "new_visitors", title: "New Visitors", customIcon: "celebrate", iconBg: "rgba(74,222,128,0.18)",
    description: "Visitors from the last 30 days.",
    roles: ["superadmin", "limited_admin", "pastor"] },
  { id: "recent_followups", title: "Recent Follow-Ups", customIcon: "chat", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "Your last 3 follow-up entries.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "team_activity", title: "Team Activity", customIcon: "people", iconBg: "rgba(96,165,250,0.18)",
    description: "Team member activity status.",
    roles: ["superadmin", "limited_admin"] },
  { id: "checkins", title: "Calendar", customIcon: "calendar", iconBg: "rgba(74,222,128,0.18)",
    description: "Mini calendar preview & link to the Calendar page.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "services", title: "Services", customIcon: "dove", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "Upcoming recurring services.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "appointments", title: "Appointments", customIcon: "calendar-clock", iconBg: "rgba(96,165,250,0.18)",
    description: "Your upcoming appointments.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "blockouts", title: "Blockouts", customIcon: "warning", iconBg: "rgba(255,107,107,0.18)",
    description: "Upcoming blockout dates.",
    roles: ["superadmin", "limited_admin"] },
  { id: "analytics", title: "Analytics", customIcon: "chart", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "Snapshot of key church metrics.",
    roles: ["superadmin", "limited_admin", "pastor"] },
  { id: "donations", title: "Donations", customIcon: "care-heart", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "This month's donations + open community needs.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "my_schedule", title: "My Schedule", customIcon: "calendar-highlight", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "Today + tomorrow: services, events, appointments, birthdays.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "override_summary", title: "Override Summary", customIcon: "warning", iconBg: "rgba(255,107,107,0.18)",
    description: "Active priority overrides + last 30 days of expirations.",
    roles: ["superadmin"] },
  { id: "recent_announcements", title: "Recent Announcements", customIcon: "megaphone", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "Up to 3 unread announcements across your departments.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "my_giving", title: "My Giving", customIcon: "money", iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    description: "Your giving total for the year.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
  { id: "pending_appointments", title: "Pending Appointments", customIcon: "download-inbox", iconBg: "rgba(255,107,107,0.18)",
    description: "Incoming appointment requests waiting for action — only visible to pastors, their assistants, and admins.",
    roles: ["superadmin", "limited_admin", "pastor", "team_member"] },
];

const ROLE_DEFAULT_LAYOUT = {
  superadmin:    ["my_week", "pending_appointments", "new_visitors", "recent_followups", "team_activity", "upcoming_events", "birthdays", "analytics"],
  limited_admin: ["my_week", "pending_appointments", "new_visitors", "recent_followups", "team_activity", "upcoming_events", "birthdays", "analytics"],
  pastor:        ["my_week", "pending_appointments", "new_visitors", "recent_followups", "my_attendance", "upcoming_events", "birthdays", "analytics"],
  team_member:   ["my_week", "my_giving", "recent_followups", "my_attendance", "upcoming_events", "birthdays", "checkins"],
};

function widgetMeta(id) { return WIDGET_REGISTRY.find(w => w.id === id); }
function availableWidgetsForRole(role) { return WIDGET_REGISTRY.filter(w => w.roles.includes(role)); }
function defaultLayoutForRole(role) {
  const all = availableWidgetsForRole(role).map(w => w.id);
  const def = ROLE_DEFAULT_LAYOUT[role] || [];
  // Only keep ids that exist in availability for the role
  return def.filter(id => all.includes(id));
}

// ---- Individual widget contents ------------------------------------
function BirthdaysWidget({ members, navigate }) {
  const bdays = upcomingBirthdays(members, 7);
  const openWeek = () => navigate("/birthdays-this-week");
  if (bdays.length === 0) {
    // No birthdays this week — surface the next upcoming birthday with a
    // "N days away" note. Widget itself remains clickable → /birthdays.
    const nextUp = upcomingBirthdays(members, 365).slice(0, 1);
    return (
      <div className="widget-empty" onClick={openWeek} style={{ cursor: "pointer" }} data-testid="widget-birthdays-empty">
        <div className="we-icon"><CustomIcon name="birthday-cake" size={30} /></div>
        {nextUp.length === 0
          ? "No birthdays on file."
          : <>Next up: <b>{fullName(nextUp[0])}</b> — {nextUp[0].daysUntilBirthday} day{nextUp[0].daysUntilBirthday === 1 ? "" : "s"} away.</>
        }
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--kc-accent)", fontWeight: 700 }}>Tap to see the week →</div>
      </div>
    );
  }
  return (
    <div data-testid="widget-birthdays" onClick={openWeek} style={{ cursor: "pointer" }}>
      {bdays.slice(0, 6).map(m => (
        <div key={m.id} className="widget-row" data-testid={`widget-birthday-${m.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}`); }}>
          <Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{ fontSize: 11 }} />
          <div className="widget-row-main">
            <div className="widget-row-title">{fullName(m)}</div>
            <div className="widget-row-meta">{m.hideBirthYear ? formatMonthDay(m.dob) : formatDate(m.dob)}</div>
          </div>
          <div className="widget-row-trailing">
            {m.daysUntilBirthday === 0
              ? <span className="badge badge-gold" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="party" size={11} /> Today</span>
              : <span>{m.daysUntilBirthday} day{m.daysUntilBirthday === 1 ? "" : "s"}</span>}
          </div>
        </div>
      ))}
      <div style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "var(--kc-accent)", fontWeight: 700 }} data-testid="widget-birthdays-see-week">
        See this week →
      </div>
    </div>
  );
}

function MyAttendanceWidget({ currentUser, attendance, navigate }) {
  if (!currentUser?.memberId) return (
    <div className="widget-empty" data-testid="widget-myatt-nolink" style={{ padding: 20, textAlign: "center" }}>
      <div className="we-icon"><CustomIcon name="clipboard" size={30} /></div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "var(--kc-sub)", marginTop: 8, lineHeight: 1.5 }}>
        No attendance record found. Ask your administrator to link your account to your member profile.
      </div>
    </div>
  );

  // Current calendar month bounds.
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  let present = 0, absent = 0;
  (attendance || []).forEach(rec => {
    if (rec.deletedAt) return;
    if (!rec.date) return;
    const d = new Date(rec.date);
    if (d < monthStart || d >= monthEnd) return;
    const s = rec?.records?.[currentUser.memberId];
    if (s === "present") present++;
    else if (s === "absent") absent++;
  });
  const total = present + absent;
  const rate = total ? Math.round(present / total * 100) : 0;

  const box = (val, label, testid) => (
    <div data-testid={testid} style={{
      flex: "1 1 0",
      minWidth: 0,
      background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)",
      border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)",
      borderRadius: 12,
      padding: "14px 8px",
      textAlign: "center",
    }}>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 28, fontWeight: 700, color: "var(--kc-accent)", lineHeight: 1 }}>{val}</div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--kc-sub)", marginTop: 6 }}>{label}</div>
    </div>
  );

  return (
    <div className="widget-body-padded" data-testid="widget-myatt" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 10 }}>
        {box(total > 0 ? `${rate}%` : "—", "Rate", "widget-myatt-rate")}
        {box(present, "Present", "widget-myatt-present")}
        {box(absent,  "Absent",  "widget-myatt-absent")}
      </div>
      <button
        type="button"
        className="btn btn-outline btn-sm"
        data-testid="widget-myatt-view"
        onClick={() => navigate("/my-attendance")}
        style={{ alignSelf: "stretch", justifyContent: "center" }}
      >View All</button>
    </div>
  );
}

function UpcomingEventsWidget({ events, navigate }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcoming = [...events]
    .filter(e => e.date && new Date(e.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 2);
  if (upcoming.length === 0) return (
    <div className="widget-empty"><div className="we-icon"><CustomIcon name="event-ticket" size={30} /></div>No upcoming events scheduled.</div>
  );
  return (
    <div>
      {upcoming.map(e => (
        <div key={e.id} className="widget-row" data-testid={`widget-event-${e.id}`} onClick={() => navigate("/events")}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: "color-mix(in srgb, var(--kc-accent) 14%, transparent)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            color: "var(--kc-accent)", fontFamily: "'Montserrat',sans-serif", flexShrink: 0,
          }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {new Date(e.date).toLocaleDateString("en-US", { month: "short" })}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>{new Date(e.date).getDate()}</div>
          </div>
          <div className="widget-row-main">
            <div className="widget-row-title">{e.name}</div>
            <div className="widget-row-meta">{formatDate(e.date)}{e.location ? ` · ${e.location}` : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// MyWeekAtAGlanceWidget — horizontal scroll strip of next 7 days.
// Honours the user's Calendar scope toggle (kc_cal_scope_<uid>), so the
// items shown here match what the calendar shows them. Tap a day → opens
// the calendar in Day view at that date. Tap an item → opens its detail
// page. Tap the gold + → POSTs a task and shows a confirmation toast.
// ============================================================
function MyWeekAtAGlanceWidget({ currentUser, events, services, appointments, blockouts, members, setTasks, navigate }) {
  const uid = currentUser ? currentUser.id : "anon";
  // Honour the calendar's persisted scope toggle. Default "both".
  const scope = (() => {
    if (typeof window === "undefined") return "both";
    const v = window.localStorage.getItem(`kc_cal_scope_${uid}`);
    return v === "church" || v === "mine" || v === "both" ? v : "both";
  })();

  // Anchor at today (local midnight) and build 7 days.
  const days = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today); d.setDate(d.getDate() + i);
      return d;
    });
  }, []);

  const range = useMemo(() => {
    const from = days[0];
    const to = new Date(days[6]); to.setHours(23, 59, 59, 999);
    return { from, to };
  }, [days]);

  const { itemsByDate } = useMemo(
    () => buildItemsForRange({
      from: range.from, to: range.to,
      currentUser, scope, events, services, appointments, blockouts, members,
    }),
    [range, currentUser, scope, events, services, appointments, blockouts, members]
  );

  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2400); };

  const openDay = (d) => {
    if (!d) return;
    // Persist the calendar's view mode and anchor date so when we land on
    // the full calendar page it opens in Day view at this date.
    try {
      window.localStorage.setItem(`kc_cal_view_${uid}`, "day");
    } catch (_err) {
      // localStorage may be unavailable (private mode); fall back silently.
    }
    navigate("/check-ins/calendar");
  };

  const KIND_ROUTE = { event: "/events", service: "/admin/services", appointment: "/appointments", blockout: "/check-ins" };
  const KIND_ICON_NAME = { event: "event-ticket", service: "dove", appointment: "calendar-clock", blockout: "warning", birthday: "birthday-cake" };

  const openItem = (it) => {
    if (!it) return;
    if (it.kind === "birthday" && it.memberId) { navigate(`/members/${it.memberId}`); return; }
    navigate(KIND_ROUTE[it.kind] || "/check-ins/calendar");
  };

  const addToTasks = async (e, it) => {
    e.stopPropagation();
    try {
      const fresh = await apiCreateTask({
        title: it.title || it.kind,
        description: it.meta || "",
        dueDate: (it.date || "").slice(0, 10),
        repeat: "none",
        list: "Personal",
      });
      if (typeof setTasks === "function") setTasks(prev => [...prev, fresh]);
      showToast("Added to your tasks.");
    } catch (err) {
      showToast(`Could not add: ${err.message || err}`);
    }
  };

  const todayStr = ymdKey(new Date());

  return (
    <div data-testid="my-week-widget" className="my-week-strip-wrap">
      <div className="my-week-scroll" data-testid="my-week-scroll">
        {days.map((d, idx) => {
          const k = ymdKey(d);
          const items = itemsByDate[k] || [];
          const isToday = k === todayStr;
          return (
            <div
              key={k}
              role="button"
              tabIndex={0}
              className={`my-week-day ${isToday ? "is-today" : ""}`}
              data-testid={`my-week-day-${idx}`}
              onClick={() => openDay(d)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDay(d); } }}
              aria-label={`${d.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} — ${items.length} item${items.length===1?"":"s"}`}
            >
              <div className="mwd-header">
                <span className="mwd-dow">{d.toLocaleDateString("en-US",{weekday:"short"}).toUpperCase()}</span>
                <span className="mwd-num">{d.getDate()}</span>
              </div>
              <div className="mwd-body">
                {items.length === 0 ? (
                  <div className="mwd-empty" data-testid={`my-week-empty-${idx}`}>No items</div>
                ) : items.slice(0, 4).map((it, i) => (
                  <div
                    key={`${it.kind}-${it.id}-${i}`}
                    className={`mwd-item kind-${it.kind}`}
                    data-testid={`my-week-item-${idx}-${it.kind}-${it.id}`}
                    onClick={(e) => { e.stopPropagation(); openItem(it); }}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="mwd-item-ico" aria-hidden="true">{KIND_ICON_NAME[it.kind] ? <CustomIcon name={KIND_ICON_NAME[it.kind]} size={12} /> : "•"}</span>
                    <span className="mwd-item-title">{it.title}</span>
                    <button
                      type="button"
                      className="mwd-item-add"
                      data-testid={`my-week-add-task-${idx}-${it.kind}-${it.id}`}
                      onClick={(e) => addToTasks(e, it)}
                      aria-label={`Add ${it.title} to tasks`}
                      title="Add to your tasks"
                    >+</button>
                  </div>
                ))}
                {items.length > 4 && (
                  <div className="mwd-overflow" data-testid={`my-week-overflow-${idx}`}>+{items.length - 4} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {toast && (
        <div data-testid="my-week-toast" role="status" className="my-week-toast">{toast}</div>
      )}
    </div>
  );
}

function NewVisitorsWidget({ currentUser, members, users, setUsers, persist, addAudit, navigate }) {

  const [openAssign, setOpenAssign] = useState(null);
  const recentVisitors = useMemo(() => members.filter(m => {
    const isVisitor = m.memberStatus === "Guest" || (m.tags || []).includes("Visitor");
    if (!isVisitor || !m.joinedDate) return false;
    const days = Math.floor((Date.now() - new Date(m.joinedDate).getTime()) / 86400000);
    return days >= 0 && days <= 30;
  }).sort((a, b) => new Date(b.joinedDate) - new Date(a.joinedDate)), [members]);

  const teamMembers = users.filter(u => u.role === "team_member");
  const teamMemberOfVisitor = (visitorId) => users.find(u => (u.assignedMembers || []).includes(visitorId)) || null;

  const assignVisitorToTeam = async (visitorId, teamUserId) => {
    // Calculate new assignedMembers for every team member affected.
    const tmUsers = users.filter(u => u.role === "team_member");
    try {
      // Run the writes server-side (which also rebuilds the kc_users mirror).
      await Promise.all(tmUsers.map(u => {
        const filtered = (u.assignedMembers || []).filter(id => id !== visitorId);
        const newAssigned = u.id === teamUserId ? [...filtered, visitorId] : filtered;
        const original = u.assignedMembers || [];
        if (newAssigned.length === original.length && newAssigned.every((id, i) => id === original[i])) return null;
        return apiAdminUpdateUser(u.id, { assignedMembers: newAssigned });
      }).filter(Boolean));
      const updatedUsers = users.map(u => {
        if (u.role !== "team_member") return u;
        const filtered = (u.assignedMembers || []).filter(id => id !== visitorId);
        if (u.id === teamUserId) return { ...u, assignedMembers: [...filtered, visitorId] };
        return { ...u, assignedMembers: filtered };
      });
      setUsers(updatedUsers);
      const target = updatedUsers.find(u => u.id === teamUserId);
      const visitor = members.find(m => m.id === visitorId);
      if (visitor && target) addAudit(currentUser.id, currentUser.name, "Assigned visitor", `Assigned ${fullName(visitor)} to ${target.name}`);
    } catch (err) {
      alert(`Assignment failed: ${err.message || err}`);
    }
    setOpenAssign(null);
  };
  const unassignVisitor = async (visitorId) => {
    const tmUsers = users.filter(u => u.role === "team_member" && (u.assignedMembers || []).includes(visitorId));
    try {
      await Promise.all(tmUsers.map(u =>
        apiAdminUpdateUser(u.id, { assignedMembers: (u.assignedMembers || []).filter(id => id !== visitorId) })
      ));
      const updatedUsers = users.map(u => u.role !== "team_member" ? u
        : { ...u, assignedMembers: (u.assignedMembers || []).filter(id => id !== visitorId) });
      setUsers(updatedUsers);
    } catch (err) {
      alert(`Unassignment failed: ${err.message || err}`);
    }
    setOpenAssign(null);
  };

  if (recentVisitors.length === 0) return (
    <div className="widget-empty"><div className="we-icon"><CustomIcon name="dove" size={30} /></div>No new visitors in the last 30 days. Share the QR poster to welcome more.</div>
  );

  return (
    <div>
      {recentVisitors.map(v => {
        const assignedTo = teamMemberOfVisitor(v.id);
        const isOpen = openAssign === v.id;
        return (
          <div key={v.id} data-testid={`visitor-row-${v.id}`} style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexWrap: "wrap" }}>
            <div data-testid={`visitor-link-${v.id}`} className="flex items-center gap-12" role="link" tabIndex={0}
              onClick={() => navigate(`/members/${v.id}`)} onKeyDown={e => e.key === "Enter" && navigate(`/members/${v.id}`)}
              style={{ minWidth: 0, flex: "1 1 200px", padding: "4px 6px", borderRadius: 8, margin: "-4px -6px", cursor: "pointer" }}>
              <Avatar name={fullName(v)} photo={v.photo} memberId={v.id} status={v.memberStatus} style={{ fontSize: 12 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 600, color: "var(--kc-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fullName(v)}</div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "var(--kc-meta)", marginTop: 2 }}>
                  {formatDate(v.joinedDate)} · {(v.phone?.[0]?.number) || (v.email?.[0]?.address) || "—"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-8" style={{ position: "relative" }}>
              {assignedTo
                ? <span className="badge badge-gold" data-testid={`visitor-assigned-${v.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="person" size={11} /> {assignedTo.name}</span>
                : <span className="badge badge-charcoal">Unassigned</span>}
              <button data-testid={`visitor-assign-btn-${v.id}`} className="btn btn-sm btn-outline" onClick={() => setOpenAssign(isOpen ? null : v.id)}>
                {assignedTo ? "Reassign" : "Assign"} ▾
              </button>
              {isOpen && (
                <>
                  <div onClick={() => setOpenAssign(null)} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
                  <div data-testid={`visitor-assign-menu-${v.id}`} style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, minWidth: 240, background: "var(--kc-elevated)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.6)", zIndex: 51, padding: 6 }}>
                    <div style={{ padding: "8px 12px 4px", fontSize: 10, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Assign to team member</div>
                    {teamMembers.length === 0 && <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--kc-meta)", fontFamily: "'Montserrat',sans-serif" }}>No team members yet.</div>}
                    {teamMembers.map(tm => {
                      const isCurrent = assignedTo?.id === tm.id;
                      return (
                        <button key={tm.id} data-testid={`visitor-assign-option-${v.id}-${tm.id}`} onClick={() => assignVisitorToTeam(v.id, tm.id)}
                          style={{ width: "100%", textAlign: "left", padding: "9px 12px", borderRadius: 6, border: "none", cursor: "pointer", background: isCurrent ? "color-mix(in srgb, var(--kc-accent) 12%, transparent)" : "transparent", color: isCurrent ? "var(--kc-accent)" : "#FFFFFF", fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: isCurrent ? 700 : 500, display: "flex", alignItems: "center", gap: 10 }}
                          onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                          onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}>
                          <Avatar name={tm.name} photo={tm.photo} memberId={tm.memberId} style={{ width: 24, height: 24, fontSize: 10 }} />
                          <span style={{ flex: 1 }}>{tm.name}</span>
                          <span style={{ fontSize: 10, color: "var(--kc-meta)" }}>{(tm.assignedMembers || []).length} assigned</span>
                          {isCurrent && <span style={{ color: "var(--kc-accent)", display: "inline-flex" }}><CustomIcon name="check" size={12} /></span>}
                        </button>
                      );
                    })}
                    {assignedTo && (
                      <button data-testid={`visitor-unassign-${v.id}`} onClick={() => unassignVisitor(v.id)}
                        style={{ width: "100%", textAlign: "left", padding: "9px 12px", borderRadius: 6, border: "none", cursor: "pointer", background: "transparent", color: "#ff6b6b", fontFamily: "'Montserrat',sans-serif", fontSize: 12, marginTop: 4, borderTop: "1px solid rgba(255,255,255,0.06)" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(184,50,50,0.10)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>Remove assignment</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RecentFollowupsWidget({ currentUser, followups, members, users, navigate }) {
  const mine = useMemo(() => {
    return [...followups]
      .filter(f => f.teamMemberId === currentUser.id)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 3);
  }, [followups, currentUser.id]);
  if (mine.length === 0) return (
    <div className="widget-empty"><div className="we-icon"><CustomIcon name="chat" size={30} /></div>No follow-ups assigned to you yet.</div>
  );
  return (
    <div>
      {mine.map(fu => {
        const m = members.find(x => x.id === fu.memberId);
        const u = users.find(x => x.id === fu.teamMemberId);
        return (
          <div key={fu.id} className="widget-row" data-testid={`widget-followup-${fu.id}`} onClick={() => navigate(`/follow-ups/${fu.id}`)}>
            <div className="log-dot" style={{ marginTop: 0 }} />
            <div className="widget-row-main">
              <div className="widget-row-title">{u?.name || "—"} visited <strong>{m ? fullName(m) : "Unknown"}</strong></div>
              <div className="widget-row-meta">{formatDate(fu.date)} · {fu.type}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TeamActivityWidget({ users, followups, navigate }) {
  const rows = useMemo(() => users.filter(u => u.role === "team_member").map(u => {
    const myFu = followups.filter(f => f.teamMemberId === u.id);
    const last = myFu.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    const daysSince = last ? Math.floor((new Date() - new Date(last.date)) / 86400000) : 999;
    return { ...u, lastActivity: last?.date, daysSince, flagged: daysSince > 30 };
  }), [users, followups]);
  if (rows.length === 0) return (
    <div className="widget-empty"><div className="we-icon"><CustomIcon name="people" size={30} /></div>No team members yet.</div>
  );
  return (
    <div>
      {rows.map(u => (
        <div key={u.id} className="widget-row" data-testid={`widget-team-${u.id}`} onClick={() => navigate(`/follow-ups?assignee=${u.id}`)}>
          <Avatar name={u.name} photo={u.photo} memberId={u.memberId} style={{ fontSize: 11 }} />
          <div className="widget-row-main">
            <div className="widget-row-title">{u.name}</div>
            <div className="widget-row-meta">{(u.assignedMembers || []).length} assigned · last {u.lastActivity ? formatDate(u.lastActivity) : "Never"}</div>
          </div>
          <div className="widget-row-trailing">
            {u.flagged ? <span className="badge badge-red" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="warning" size={10} /> Inactive</span> : <span className="badge badge-green">Active</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function CheckinsWidget({ attendance, navigate }) {
  // Mini calendar — current month, with green dots on days that have an attendance record
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const checkedDays = new Set(attendance
    .map(a => a.date)
    .filter(d => d && new Date(d).getMonth() === month && new Date(d).getFullYear() === year)
    .map(d => new Date(d).getDate()));
  const today = now.getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div className="widget-body-padded">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {now.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </div>
        <span style={{ fontSize: 11, color: "var(--kc-meta)", fontFamily: "'Montserrat',sans-serif" }}>{checkedDays.size} session{checkedDays.size === 1 ? "" : "s"}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={`h${i}`} style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, color: "var(--kc-meta)", textAlign: "center", padding: "4px 0", textTransform: "uppercase", letterSpacing: "0.06em" }}>{d}</div>
        ))}
        {cells.map((c, i) => {
          if (c === null) return <div key={`e${i}`} />;
          const isToday = c === today;
          const has = checkedDays.has(c);
          return (
            <div key={`d${i}`} style={{
              aspectRatio: "1/1", display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: 6, fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: isToday ? 700 : 500,
              background: isToday ? "color-mix(in srgb, var(--kc-accent) 18%, transparent)" : "transparent",
              border: isToday ? "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)" : "1px solid transparent",
              color: isToday ? "var(--kc-accent)" : has ? "#FFFFFF" : "rgba(255,255,255,0.55)",
              position: "relative",
            }}>
              {c}
              {has && <span style={{ position: "absolute", bottom: 2, width: 4, height: 4, borderRadius: 50, background: "#4ade80" }} />}
            </div>
          );
        })}
      </div>
      <button className="btn btn-outline btn-sm w-full" data-testid="widget-checkins-view" style={{ marginTop: 12, justifyContent: "center" }} onClick={() => navigate("/check-ins")}>Open Calendar →</button>
    </div>
  );
}

function ServicesWidget({ services, navigate }) {
  if (!services || services.length === 0) return (
    <div className="widget-empty"><div className="we-icon"><CustomIcon name="dove" size={30} /></div>No services scheduled yet.</div>
  );
  return (
    <div>
      {services.slice(0, 4).map(s => (
        <div key={s.id} className="widget-row" data-testid={`widget-service-${s.id}`} onClick={() => navigate("/admin/services")}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "color-mix(in srgb, var(--kc-accent) 14%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--kc-accent)", flexShrink: 0 }}><CustomIcon name="dove" size={20} /></div>
          <div className="widget-row-main">
            <div className="widget-row-title">{s.name}</div>
            <div className="widget-row-meta">{s.schedule || s.day || "Weekly"}{s.time ? ` · ${s.time}` : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AppointmentsWidget({ currentUser, appointments, navigate }) {
  const mine = (appointments || []).filter(a => a && a.userId === currentUser.id && a.date && new Date(a.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  if (mine.length === 0) return (
    <div className="widget-empty">
      <div className="we-icon"><CustomIcon name="calendar-clock" size={30} /></div>
      No upcoming appointments.
      <div style={{ marginTop: 10 }}><button className="btn btn-outline btn-sm" onClick={() => navigate("/appointments")}>Open Appointments</button></div>
    </div>
  );
  return (
    <div>
      {mine.slice(0, 4).map(a => (
        <div key={a.id} className="widget-row" data-testid={`widget-appt-${a.id}`} onClick={() => navigate("/appointments")}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(96,165,250,0.14)", display: "flex", alignItems: "center", justifyContent: "center", color: "#60a5fa", flexShrink: 0 }}><CustomIcon name="calendar-clock" size={20} /></div>
          <div className="widget-row-main">
            <div className="widget-row-title">{a.title || "Appointment"}</div>
            <div className="widget-row-meta">{formatDate(a.date)}{a.with ? ` · ${a.with}` : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BlockoutsWidget({ navigate }) {
  return (
    <div className="widget-empty">
      <div className="we-icon"><CustomIcon name="warning" size={30} /></div>
      No upcoming blockouts.
      <div style={{ marginTop: 10 }}><button className="btn btn-outline btn-sm" onClick={() => navigate("/dashboard")}>Set Blockout (coming soon)</button></div>
    </div>
  );
}

function DonationsWidget({ navigate }) {
  const [state, setState] = useState({ loading: true, denied: false, data: null, openNeeds: 0, err: false });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const today = new Date();
        const from = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
        const to = today.toISOString().slice(0, 10);
        const [{ apiDonationsReport, apiListNeeds }] = await Promise.all([import("../donations/api")]);
        const [report, needs] = await Promise.all([
          apiDonationsReport({ from, to, nameMode: "anonymous" }).catch(e => { if (e.message === "403") return "__denied__"; throw e; }),
          apiListNeeds().catch(() => null),
        ]);
        if (!alive) return;
        if (report === "__denied__") { setState({ loading: false, denied: true, data: null, openNeeds: 0, err: false }); return; }
        const openNeeds = Array.isArray(needs) ? needs.filter(n => n && n.status === "Open").length : 0;
        setState({ loading: false, denied: false, data: report, openNeeds, err: false });
      } catch (_) {
        if (alive) setState({ loading: false, denied: false, data: null, openNeeds: 0, err: true });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state.loading) return <div className="widget-empty"><div className="we-icon"><CustomIcon name="care-heart" size={30} /></div>Loading donations…</div>;
  if (state.denied)  return <div className="widget-empty"><div className="we-icon"><CustomIcon name="lock" size={30} /></div>You don&apos;t have access to the Donations workspace.</div>;
  if (state.err || !state.data) return <div className="widget-empty"><div className="we-icon"><CustomIcon name="care-heart" size={30} /></div>Could not load donations summary.</div>;

  const s = state.data.summary || {};
  const monthLabel = new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const stats = [
    { label: `Donations · ${monthLabel}`, value: s.totalCount || 0,                                hint: "this month",        color: "var(--kc-accent)" },
    { label: "Est. value",                value: `$${Number(s.totalValue || 0).toFixed(0)}`,      hint: "this month",        color: "#4ade80" },
    { label: "Open community needs",      value: state.openNeeds,                                  hint: "currently open",    color: "#60a5fa" },
  ];
  return (
    <div className="widget-body-padded">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {stats.map(st => (
          <div key={st.label} data-testid={`donations-widget-stat-${st.label.split(" ")[0].toLowerCase()}`} style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", borderRadius: 10, padding: 12, textAlign: "center" }}>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: st.color, lineHeight: 1 }}>{st.value}</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "var(--kc-sub)", marginTop: 6, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{st.label}</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "var(--kc-meta)", marginTop: 4 }}>{st.hint}</div>
          </div>
        ))}
      </div>
      <button className="btn btn-outline btn-sm w-full" data-testid="widget-donations-view" style={{ marginTop: 12, justifyContent: "center" }} onClick={() => navigate("/donations")}>Open Donations →</button>
    </div>
  );
}

function AnalyticsWidget({ members, attendance, services, followups, navigate }) {
  const total = members.length;
  const active = members.filter(m => m.memberStatus === "Active").length;

  // Last service attendance % — pick the most recent attendance record
  // and compute its present rate (rather than averaging across all-time).
  const lastAtt = (attendance || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  let lastRate = null, lastSvcName = "";
  if (lastAtt) {
    const vals = Object.values(lastAtt.records || {});
    const present = vals.filter(v => v === "present").length;
    if (vals.length > 0) lastRate = Math.round((present / vals.length) * 100);
    const svc = (services || []).find(s => s.id === lastAtt.serviceId);
    if (svc) lastSvcName = svc.name;
  }

  // Active follow-ups = anything not archived / not pending_closure.
  const activeFus = (followups || []).filter(f => f.status !== "archived" && f.status !== "pending_closure").length;

  const stats = [
    { label: "Members",         value: total,                                              hint: `${active} active`,                                                color: "var(--kc-accent)" },
    { label: "Last Service",    value: lastRate === null ? "—" : `${lastRate}%`,           hint: lastSvcName || (lastAtt ? "Most recent" : "No data yet"),          color: "#4ade80" },
    { label: "Active Follow-ups", value: activeFus,                                        hint: `${(followups || []).length} total`,                              color: "#60a5fa" },
  ];
  return (
    <div className="widget-body-padded">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", borderRadius: 10, padding: 12, textAlign: "center" }}>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "var(--kc-sub)", marginTop: 6, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{s.label}</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "var(--kc-meta)", marginTop: 4 }}>{s.hint}</div>
          </div>
        ))}
      </div>
      <button className="btn btn-outline btn-sm w-full" data-testid="widget-analytics-view" style={{ marginTop: 12, justifyContent: "center" }} onClick={() => navigate("/reports/analytics")}>Open Analytics →</button>
    </div>
  );
}

function MyScheduleWidget({ events, services, appointments, members, currentUser, navigate }) {
  // Today + tomorrow chronological timeline.
  const items = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const dayAfter = new Date(today); dayAfter.setDate(today.getDate() + 2);
    const fmtTime = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    };
    const out = [];
    // Events with date in [today, dayAfter)
    (events || []).forEach(e => {
      const d = new Date(e.date);
      if (d >= today && d < dayAfter) {
        out.push({ when: d, time: fmtTime(e.date), iconName: "party", kind: "event", label: e.name, link: "/events" });
      }
    });
    // Services — recurring weekly. Show next occurrence falling in today/tomorrow.
    (services || []).forEach(s => {
      const dow = typeof s.dayOfWeek === "number" ? s.dayOfWeek : null;
      [0, 1].forEach(off => {
        const d = new Date(today); d.setDate(today.getDate() + off);
        if (d.getDay() === dow) {
          const [h, mm] = (s.time || "09:00").split(":").map(Number);
          const when = new Date(d); when.setHours(h || 0, mm || 0, 0, 0);
          out.push({ when, time: fmtTime(when.toISOString()), iconName: "dove", kind: "service", label: s.name, link: "/admin/services" });
        }
      });
    });
    // Appointments — user's only
    (appointments || []).forEach(a => {
      if (a.userId !== currentUser?.id) return;
      const d = new Date(a.date);
      if (d >= today && d < dayAfter) {
        out.push({ when: d, time: fmtTime(a.date), iconName: "calendar-clock", kind: "appointment", label: a.title || "Appointment", link: "/tasks" });
      }
    });
    // Birthdays — today/tomorrow only
    const monthDayOf = (dt) => `${dt.getMonth()}-${dt.getDate()}`;
    const todayMD = monthDayOf(today), tmwMD = monthDayOf(tomorrow);
    (members || []).forEach(m => {
      if (!m.dob) return;
      const dob = parseDateOnly(m.dob);
      if (!dob) return;
      const md = monthDayOf(dob);
      if (md === todayMD || md === tmwMD) {
        const dayDate = md === todayMD ? today : tomorrow;
        const when = new Date(dayDate); when.setHours(8, 0, 0, 0);
        out.push({ when, time: "All day", iconName: "birthday-cake", kind: "birthday", label: `${m.firstName} ${m.lastName}`, link: `/members/${m.id}` });
      }
    });
    return out.sort((a, b) => a.when - b.when).slice(0, 8);
  }, [events, services, appointments, members, currentUser]);

  return (
    <div className="widget-body-padded">
      {items.length === 0 ? (
        <div className="widget-empty"><div className="we-icon"><CustomIcon name="calendar-highlight" size={30} /></div>Nothing on the schedule for today or tomorrow.</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((it, i) => (
            <li
              key={`${it.kind}-${i}`}
              data-testid={`my-schedule-item-${i}`}
              onClick={() => navigate(it.link)}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)", cursor: "pointer" }}
            >
              <span style={{ fontSize: 16, display: "inline-flex", alignItems: "center" }}>{it.iconName ? <CustomIcon name={it.iconName} size={16} /> : it.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontFamily: "'Montserrat',sans-serif", color: "var(--kc-text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.label}</div>
                <div style={{ fontSize: 10, color: "var(--kc-meta)" }}>
                  {it.when.toLocaleDateString() === new Date().toLocaleDateString() ? "Today" : "Tomorrow"} · {it.time} · <span style={{ color: "var(--kc-accent)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{it.kind}</span>
                </div>
              </div>
              <span style={{ color: "var(--kc-meta)" }}>›</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OverrideSummaryWidget({ departments, navigate }) {
  const now = Date.now();
  const THIRTY_D = 30 * 86400 * 1000;
  let active = 0, expiredRecent = 0;
  const rows = [];
  (departments || []).forEach(d => {
    if (d.overrideActive) {
      active += 1;
      rows.push({ id: d.id, name: d.name, status: "active" });
    }
    (d.priorityOverrideHistory || []).forEach(h => {
      const ts = Date.parse(h.expiredAt || h.endAt || h.startAt || "");
      if (Number.isFinite(ts) && now - ts <= THIRTY_D) expiredRecent += 1;
    });
  });
  return (
    <div className="widget-body-padded">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 10, padding: 12, textAlign: "center" }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 24, fontWeight: 700, color: "var(--kc-accent)" }}>{active}</div>
          <div style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>Active now</div>
        </div>
        <div style={{ background: "var(--kc-surface)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: 12, textAlign: "center" }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 24, fontWeight: 700, color: "var(--kc-text)" }}>{expiredRecent}</div>
          <div style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>Expired (30d)</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="widget-empty" style={{ padding: 16 }}>No active overrides.</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.slice(0, 4).map(r => (
            <li key={r.id} data-testid={`override-summary-row-${r.id}`} onClick={() => navigate(`/departments/${r.id}`)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", borderRadius: 8, background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", cursor: "pointer" }}>
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 600, color: "var(--kc-text)" }}>{r.name}</span>
              <span style={{ fontFamily: "'Cinzel',serif", fontSize: 10, color: "var(--kc-accent)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>View history →</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecentAnnouncementsWidget({ currentUser, members, departments, navigate }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Find the user's dept names via their linked member record.
        const me = (members || []).find(m => m.id === currentUser?.memberId);
        const userDeptNames = me?.departments || [];
        const userDeptIds = (departments || []).filter(d => userDeptNames.includes(d.name)).map(d => d.id);
        if (userDeptIds.length === 0) { if (alive) setItems([]); return; }
        const { apiListAnnouncements } = await import("../departments/api");
        const lists = await Promise.all(userDeptIds.map(id => apiListAnnouncements(id).catch(() => [])));
        const flat = [].concat(...lists);
        const unread = flat.filter(a => !(Array.isArray(a.readBy) ? a.readBy.includes(currentUser?.id) : false));
        unread.sort((a, b) => (b.postedAt || "").localeCompare(a.postedAt || ""));
        if (alive) setItems(unread.slice(0, 3));
      } catch {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, [currentUser, members, departments]);

  return (
    <div className="widget-body-padded">
      {items.length === 0 ? (
        <div className="widget-empty"><div className="we-icon"><CustomIcon name="megaphone" size={30} /></div>No unread announcements.</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map(a => (
            <li key={a.id} data-testid={`recent-ann-${a.id}`} onClick={() => navigate(`/departments/${a.departmentId}`)} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: 8, background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", cursor: "pointer" }}>
              <span style={{ marginTop: 6, width: 8, height: 8, borderRadius: "50%", background: "var(--kc-accent)", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Cinzel',serif", fontSize: 10, color: "var(--kc-accent)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{a.departmentName || ""}</div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 600, color: "var(--kc-text)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title || a.message?.slice(0, 60)}</div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "var(--kc-meta)", marginTop: 2 }}>{a.postedAt ? new Date(a.postedAt).toLocaleString() : ""}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MyGivingWidget({ currentUser, navigate }) {
  // Iter 84 — read user prefs (period + show-diff) from the profile.
  const mgw = (currentUser?.preferences?.myGivingWidget && typeof currentUser.preferences.myGivingWidget === "object")
    ? currentUser.preferences.myGivingWidget
    : {};
  const widgetPeriod = ["Weekly","Monthly","Yearly"].includes(mgw.period) ? mgw.period : "Yearly";
  const showDifference = mgw.showDifference === false ? false : true;
  const periodLabel = widgetPeriod === "Weekly" ? "This week" : widgetPeriod === "Monthly" ? "This month" : "This year";
  const [data, setData] = useState({ personal: 0, official: 0, last: null, loading: true });
  useEffect(() => {
    if (!currentUser?.id) { setData({ personal: 0, official: 0, last: null, loading: false }); return; }
    let alive = true;
    (async () => {
      try {
        const [{ apiListGiving }, { apiListGiving: apiListFinanceGiving }] = await Promise.all([
          import("../giving"),
          import("../finance/api"),
        ]);
        const { parseLocalDate } = await import("../../lib/localDate");
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth();
        // Compute a bounds check for the selected period.
        const inPeriod = (iso) => {
          const d = parseLocalDate(iso);
          if (!d) return false;
          if (widgetPeriod === "Weekly") {
            const start = new Date(); start.setHours(0,0,0,0); start.setDate(start.getDate() - 6);
            const end = new Date(); end.setHours(23,59,59,999);
            return d >= start && d <= end;
          }
          if (widgetPeriod === "Monthly") {
            return d.getFullYear() === y && d.getMonth() === m;
          }
          // Yearly
          return d.getFullYear() === y;
        };
        // Personal ledger (private).
        const rows = await apiListGiving(currentUser.id);
        const personalRows = (rows || []).filter(r => inPeriod(r.date));
        const personal = personalRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const last = (rows || [])[0] || null;

        // Official Finance-recorded giving for this member — same period.
        let official = 0;
        const targetMemberId = currentUser.memberId;
        if (targetMemberId) {
          try {
            const fin = await apiListFinanceGiving({ isVoided: "false", memberId: targetMemberId });
            official = (fin || [])
              .filter(r => inPeriod(r.date))
              .reduce((s, r) => s + (Number(r.amountCents) || 0), 0) / 100;
          } catch { /* silently ignore — non-Finance calls may 403 in rare edge cases */ }
        }

        if (alive) setData({ personal, official, last, loading: false });
      } catch {
        if (alive) setData({ personal: 0, official: 0, last: null, loading: false });
      }
    })();
    return () => { alive = false; };
  }, [currentUser, widgetPeriod]);

  return (
    <div className="widget-body-padded" onClick={() => navigate("/giving")} style={{ cursor: "pointer" }}>
      <div style={{ textAlign: "center", padding: "10px 0 6px" }}>
        <div data-testid="widget-giving-period-label" style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
          {periodLabel}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, alignItems: "start" }}>
          <div
            data-testid="widget-giving-official"
            style={{ background: "color-mix(in srgb, var(--kc-accent) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 10, padding: "10px 6px" }}
          >
            <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>Official</div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-accent)", lineHeight: 1.1, marginTop: 3 }}>
              ${Number(data.official || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 9, opacity: 0.55, marginTop: 3 }}>Finance-recorded</div>
          </div>
          <div
            data-testid="widget-giving-personal"
            style={{ background: "rgba(0,0,0,0.28)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 10, padding: "10px 6px" }}
          >
            <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>Personal</div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-accent)", lineHeight: 1.1, marginTop: 3 }}>
              ${Number(data.personal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 9, opacity: 0.55, marginTop: 3 }}>Your ledger</div>
          </div>
        </div>
        {showDifference && (
          <div data-testid="widget-giving-recon" style={{ marginTop: 10 }}>
            {(() => {
              const diff = Math.round((data.official - data.personal) * 100) / 100;
              const matches = Math.abs(diff) < 0.005;
              const fmt = (n) => `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              return (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 999,
                  background: matches ? "rgba(74,222,128,0.12)" : "rgba(245,158,11,0.12)",
                  border: `1px solid ${matches ? "rgba(74,222,128,0.40)" : "rgba(245,158,11,0.40)"}`,
                  color: matches ? "#4ade80" : "#f59e0b",
                  fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 11,
                }}>
                  <CustomIcon name={matches ? "check" : "warning"} size={12} />
                  <span data-testid={matches ? "widget-giving-recon-match" : "widget-giving-recon-diff"}>
                    {matches ? `Records match — ${fmt(data.official)}` : `Difference of ${fmt(diff)}`}
                  </span>
                </div>
              );
            })()}
          </div>
        )}
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "var(--kc-sub)", marginTop: 6 }}>
          {data.loading
            ? "Loading…"
            : data.last
              ? `Last personal entry: $${Number(data.last.amount).toFixed(2)} (${data.last.category}) · ${data.last.date}`
              : "No personal entries yet."}
        </div>
      </div>
      <button className="btn btn-outline btn-sm w-full" data-testid="widget-giving-view" style={{ justifyContent: "center", marginTop: 8 }} onClick={(e) => { e.stopPropagation(); navigate("/giving"); }}>Open My Giving →</button>
    </div>
  );
}

// ---- Pending Appointments widget -----------------------------------
// Visible to pastors, their assigned assistants, and admins. Counts
// pending (non-confirmed, non-cancelled) appointments where the
// current user is the invited pastor OR a routed-to assistant.
function PendingAppointmentsWidget({ currentUser, isSuperAdmin, appointments, navigate }) {
  const { settings: appSettings } = useAppSettings();
  const shortName = appSettings?.apptShortName || "On-the-Fly Connections";
  const longName  = appSettings?.apptLongName  || "Weekday Connections";
  const role = currentUser?.role;
  const uid = currentUser?.id;
  const isAdmin = isSuperAdmin || role === "limited_admin";
  const isPastor = role === "pastor";

  const candidates = (appointments || []).filter(a => {
    if (a.status === "cancelled" || a.status === "confirmed") return false;
    if (isAdmin) return true;
    if (isPastor && a.pastorId === uid) return true;
    const routed = a.routedToAssistants || [];
    return routed.includes(uid);
  });

  // Regular team-members who are NOT assistants to any pastor → render
  // nothing (widget should be invisible to them).
  if (!isAdmin && !isPastor) {
    const isAssistantSomewhere = (appointments || []).some(a => (a.routedToAssistants || []).includes(uid));
    if (!isAssistantSomewhere) {
      return <div className="widget-empty" data-testid="pending-appts-not-applicable">This widget is for pastors, their assistants, and admins.</div>;
    }
  }

  const crisis = candidates.filter(a => a.isCrisis).length;
  const shortCount = candidates.filter(a => a.apptType === "short").length;
  const longCount  = candidates.filter(a => a.apptType === "long").length;
  const total = candidates.length;

  return (
    <div data-testid="widget-pending-appointments">
      <div style={{ textAlign: "center", padding: "8px 0 14px" }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 48, fontWeight: 700, color: "var(--kc-accent)", lineHeight: 1 }} data-testid="pending-appts-total">{total}</div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "var(--kc-sub)", marginTop: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>Pending request{total === 1 ? "" : "s"}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 10, padding: 10, textAlign: "center" }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)" }} data-testid="pending-appts-short">{shortCount}</div>
          <div style={{ fontSize: 10, color: "var(--kc-sub)", marginTop: 4 }}>{shortName}</div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 10, padding: 10, textAlign: "center" }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)" }} data-testid="pending-appts-long">{longCount}</div>
          <div style={{ fontSize: 10, color: "var(--kc-sub)", marginTop: 4 }}>{longName}</div>
        </div>
      </div>
      {crisis > 0 && (
        <div data-testid="pending-appts-crisis" style={{ background: "rgba(255,107,107,0.12)", border: "1px solid rgba(255,107,107,0.50)", color: "#ff9b9b", borderRadius: 10, padding: "8px 10px", marginBottom: 10, fontSize: 12, textAlign: "center", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="warning" size={13} /> {crisis} crisis request{crisis === 1 ? "" : "s"}</span>
        </div>
      )}
      <button
        className="btn btn-gold btn-sm w-full"
        data-testid="widget-pending-appts-view"
        style={{ justifyContent: "center" }}
        onClick={(e) => { e.stopPropagation(); navigate("/appointments/manage?tab=admin-tasks"); }}
      >View Administrative Tasks →</button>
    </div>
  );
}



function renderWidgetContent(id, ctx) {
  if (typeof id === "string" && id.startsWith("dept:")) {
    const deptId = id.slice(5);
    const dept = (ctx.departments || []).find(d => d.id === deptId);
    if (!dept) return <div className="widget-empty">This department is no longer available.</div>;
    const memberCount = Array.isArray(dept.users) ? dept.users.length : 0;
    const leaderUser = (ctx.users || []).find(u => u.id === dept.leaderUserId);
    const leaderLinkedMember = leaderUser && leaderUser.memberId ? (ctx.members || []).find(m => m.id === leaderUser.memberId) : null;
    const leaderName = leaderLinkedMember
      ? `${leaderLinkedMember.firstName} ${leaderLinkedMember.lastName}`
      : (leaderUser ? (leaderUser.name || leaderUser.username || "") : "");
    return <DepartmentWidget dept={dept} memberCount={memberCount} leader={leaderName} navigate={ctx.navigate} />;
  }
  switch (id) {
    case "birthdays":         return <BirthdaysWidget {...ctx} />;
    case "my_attendance":     return <MyAttendanceWidget {...ctx} />;
    case "upcoming_events":   return <UpcomingEventsWidget {...ctx} />;
    case "my_week":           return <MyWeekAtAGlanceWidget {...ctx} />;
    case "new_visitors":      return <NewVisitorsWidget {...ctx} />;
    case "recent_followups":  return <RecentFollowupsWidget {...ctx} />;
    case "team_activity":     return <TeamActivityWidget {...ctx} />;
    case "checkins":          return <CheckinsWidget {...ctx} />;
    case "services":          return <ServicesWidget {...ctx} />;
    case "appointments":      return <AppointmentsWidget {...ctx} />;
    case "blockouts":         return <BlockoutsWidget {...ctx} />;
    case "analytics":         return <AnalyticsWidget {...ctx} />;
    case "donations":         return <DonationsWidget {...ctx} />;
    case "my_schedule":       return <MyScheduleWidget {...ctx} />;
    case "override_summary":  return <OverrideSummaryWidget {...ctx} />;
    case "recent_announcements": return <RecentAnnouncementsWidget {...ctx} />;
    case "my_giving":         return <MyGivingWidget {...ctx} />;
    case "pending_appointments": return <PendingAppointmentsWidget {...ctx} />;
    default: return <div className="widget-empty">Unknown widget.</div>;
  }
}

// ---- WidgetCard wrapper --------------------------------------------
function WidgetCard({ widgetId, index, total, ctx, onRemove, onMove, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isDropTarget }) {
  const { settings: appSettings } = useAppSettings();
  let meta = widgetMeta(widgetId);
  if (meta && widgetId === "recent_followups") {
    meta = { ...meta, title: `Recent ${appSettings.followUpsLabel}` };
  }
  if (!meta && typeof widgetId === "string" && widgetId.startsWith("dept:")) {
    const dept = (ctx.departments || []).find(d => d.id === widgetId.slice(5));
    meta = {
      id: widgetId,
      title: dept ? dept.name : "Department",
      customIcon: "tools",
      iconBg: "color-mix(in srgb, var(--kc-accent) 18%, transparent)",
    };
  }
  if (!meta) return null;
  return (
    <Card
      as="section"
      className={`widget-card ${isDragging ? "is-dragging" : ""} ${isDropTarget ? "is-drop-target" : ""}`}
      data-testid={`widget-card-${widgetId}`}
      draggable
      onDragStart={(e) => onDragStart(index, e)}
      onDragOver={(e) => onDragOver(index, e)}
      onDrop={(e) => onDrop(index, e)}
      onDragEnd={onDragEnd}
    >
      <header className="widget-header">
        <div className="widget-drag-handle" data-testid={`widget-drag-${widgetId}`} aria-label="Drag to reorder" title="Drag to reorder">⋮⋮</div>
        <div className="widget-icon-box" style={{ background: meta.iconBg, color: "var(--kc-text)" }}>{meta.customIcon ? <CustomIcon name={meta.customIcon} size={20} /> : meta.icon}</div>
        <div className="widget-title text-card-primary">{meta.title}</div>
        <div className="widget-controls">
          <button type="button" className="widget-ctrl-btn" data-testid={`widget-up-${widgetId}`} aria-label="Move up"
            disabled={index === 0} onClick={() => onMove(index, -1)}>↑</button>
          <button type="button" className="widget-ctrl-btn" data-testid={`widget-down-${widgetId}`} aria-label="Move down"
            disabled={index === total - 1} onClick={() => onMove(index, 1)}>↓</button>
          <button type="button" className="widget-ctrl-btn remove" data-testid={`widget-remove-${widgetId}`} aria-label="Remove widget" title="Remove widget"
            onClick={() => onRemove(widgetId)}><CustomIcon name="close" size={12} /></button>
        </div>
      </header>
      <div className="widget-body">
        {renderWidgetContent(widgetId, ctx)}
      </div>
    </Card>
  );
}

// ---- Customize Dashboard Modal -------------------------------------
function CustomizeDashboardModal({ role, currentLayout, availableDepartments, onClose, onAdd, onRemove, onReset }) {
  const available = availableWidgetsForRole(role);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" data-testid="customize-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div className="modal-title">Customize Dashboard</div>
          <button className="modal-close" data-testid="customize-close" onClick={onClose} aria-label="Close"><CustomIcon name="close" size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-sub)", marginBottom: 14, lineHeight: 1.5 }}>
            Add the widgets you want on your dashboard or tap an existing one to remove it. Your layout is saved automatically.
          </div>
          <div className="picker-list">
            {available.map(w => {
              const added = currentLayout.includes(w.id);
              return (
                <div key={w.id} className={`picker-row ${added ? "is-added" : ""}`} data-testid={`picker-row-${w.id}`}>
                  <div className="picker-icon" style={{ background: w.iconBg }}>{w.customIcon ? <CustomIcon name={w.customIcon} size={18} /> : w.icon}</div>
                  <div className="picker-info">
                    <div className="picker-name">{w.title}</div>
                    <div className="picker-desc">{w.description}</div>
                  </div>
                  {added ? (
                    <button className="picker-action added" data-testid={`picker-action-${w.id}`} onClick={() => onRemove(w.id)} title="Remove from dashboard" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <CustomIcon name="check" size={12} /> Added
                    </button>
                  ) : (
                    <button className="picker-action" data-testid={`picker-action-${w.id}`} onClick={() => onAdd(w.id)}>
                      + Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {availableDepartments && availableDepartments.length > 0 && (
            <>
              <div style={{
                marginTop: 22, marginBottom: 10,
                fontSize: 11, letterSpacing: "0.1em", color: "var(--kc-accent)", fontWeight: 800,
                textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif",
              }}>
                Departments
              </div>
              <div className="picker-list" data-testid="customize-dept-list">
                {availableDepartments.map(d => {
                  const wid = `dept:${d.id}`;
                  const added = currentLayout.includes(wid);
                  return (
                    <div key={wid} className={`picker-row ${added ? "is-added" : ""}`} data-testid={`picker-row-${wid}`}>
                      <div className="picker-icon" style={{ background: "color-mix(in srgb, var(--kc-accent) 18%, transparent)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                        {d.logoUrl ? <img src={d.logoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} /> : <CustomIcon name="tools" size={18} />}
                      </div>
                      <div className="picker-info">
                        <div className="picker-name">{d.name}</div>
                        <div className="picker-desc">
                          Priority {(d.effectivePriority || d.priority || "medium")}
                          {d.overrideActive ? " · override active" : ""}
                        </div>
                      </div>
                      {added ? (
                        <button className="picker-action added" data-testid={`picker-action-${wid}`} onClick={() => onRemove(wid)} title="Remove from dashboard" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <CustomIcon name="check" size={12} /> Added
                        </button>
                      ) : (
                        <button className="picker-action" data-testid={`picker-action-${wid}`} onClick={() => onAdd(wid)}>
                          + Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="picker-footer-action" data-testid="customize-reset" onClick={onReset}>Reset to default</button>
          <button className="btn btn-gold" data-testid="customize-done" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ---- DashboardPage --------------------------------------------------
function DashboardPage({ currentUser, members, setMembers, followups, attendance, users, setUsers, services, events, appointments, blockouts, tasks, setTasks, notifications, visibleMembers, isSuperAdmin, isLimitedAdmin, isPastor, isTeamMember, persist, addAudit, markNotifRead, navigate, dashboardLayouts, setDashboardLayouts }) {
  const { settings: appSettings } = useAppSettings();
  const role = currentUser.role;
  const allowedIds = useMemo(() => new Set(availableWidgetsForRole(role).map(w => w.id)), [role]);

  // Live departments are fetched here so dept-widget tiles always reflect
  // the latest effective priority / override / member counts.
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    let alive = true;
    apiListDepartments().then(d => { if (alive) setDepartments(Array.isArray(d) ? d : []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Resolve current user's layout — fall back to role default, then sanitize against role-allowed ids.
  // Migration: if a user has a stored layout from before "my_week" existed, auto-prepend it so the
  // new widget surfaces without forcing a manual "Reset to defaults". Idempotent — only adds if missing.
  const storedLayout = dashboardLayouts && dashboardLayouts[currentUser.id];
  const rawLayout = (() => {
    if (!storedLayout) return defaultLayoutForRole(role);
    if (allowedIds.has("my_week") && !storedLayout.includes("my_week")) {
      return ["my_week", ...storedLayout];
    }
    return storedLayout;
  })();
  const isDeptWidgetId = (id) => typeof id === "string" && id.startsWith("dept:");
  // Filter: keep registry-allowed ids OR `dept:{id}` for live departments only.
  const layout = useMemo(() => {
    const deptIds = new Set(departments.map(d => `dept:${d.id}`));
    return (rawLayout || []).filter(id => allowedIds.has(id) || (isDeptWidgetId(id) && deptIds.has(id)));
  }, [rawLayout, allowedIds, departments]);

  const saveLayout = useCallback((nextIds) => {
    const next = { ...dashboardLayouts, [currentUser.id]: nextIds };
    setDashboardLayouts(next);
    persist("kc_dashboard_layouts", next);
  }, [dashboardLayouts, setDashboardLayouts, persist, currentUser.id]);

  const [showCustomize, setShowCustomize] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);

  const onDragStart = (i, e) => {
    setDragIndex(i);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); } catch (_err) { /* dataTransfer unavailable in older browsers */ }
  };
  const onDragOver = (i, e) => {
    e.preventDefault();
    if (dragIndex === null || i === dragIndex) return;
    setDropTargetIndex(i);
  };
  const onDrop = (i, e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) { setDragIndex(null); setDropTargetIndex(null); return; }
    const next = [...layout];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    saveLayout(next);
    setDragIndex(null); setDropTargetIndex(null);
  };
  const onDragEnd = () => { setDragIndex(null); setDropTargetIndex(null); };

  const moveByOffset = (i, offset) => {
    const j = i + offset;
    if (j < 0 || j >= layout.length) return;
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    saveLayout(next);
  };
  const removeWidget = (id) => { saveLayout(layout.filter(x => x !== id)); };
  const addWidget = (id) => {
    const isDept = typeof id === "string" && id.startsWith("dept:");
    if ((!allowedIds.has(id) && !isDept) || layout.includes(id)) return;
    saveLayout([...layout, id]);
  };
  const resetLayout = () => { saveLayout(defaultLayoutForRole(role)); };

  // Greeting
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const bdayNotifs = notifications.filter(n => {
    if (n.type !== "birthday") return false;
    const isRead = Array.isArray(n.read) ? n.read.includes(currentUser.id) : !!n.read;
    return !isRead;
  });

  // ----------------------------------------------------------------
  // Iter-74 — Day-of-week hero image (Super Admin uploaded).
  // ----------------------------------------------------------------
  // dashboardHeroImages: { sunday: {image, zoom, position, overlay}, ... }
  // Reads today's day from the user's browser and pulls the matching slot.
  // If no image is set for the current day the hero card falls back to
  // the accent gradient (existing behaviour) — no image is rendered.
  // ----------------------------------------------------------------
  const heroImage = React.useMemo(() => {
    const map = (appSettings && appSettings.dashboardHeroImages) || {};
    const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const todayKey = DAY_KEYS[new Date().getDay()];
    const slot = map[todayKey];
    if (!slot || !slot.image) return null;
    return {
      image: slot.image,
      zoom: Number.isFinite(slot.zoom) ? slot.zoom : 100,
      position: slot.position || "center",
      overlay: Number.isFinite(slot.overlay) ? slot.overlay : 40,
    };
  }, [appSettings]);

  // ----------------------------------------------------------------
  // Iter-86e — Per-day Hero card text styling (photo-driven, decoupled
  // from app light/dark theme). Reads heroTextStyles from app settings
  // for today's day of week and returns explicit inline styles that
  // must be applied to every text element inside the hero card so the
  // theme cannot bleed through var(--kc-text).
  // ----------------------------------------------------------------
  const heroTextStyle = React.useMemo(() => {
    const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const todayKey = DAY_KEYS[new Date().getDay()];
    const map = (appSettings && appSettings.heroTextStyles) || {};
    const entry = map[todayKey] || { color: "white", shadow: true, stroke: false };
    // "auto" — pick white when the overlay is heavy (>=40), else black.
    // Falls through to white when there is no hero image for the day.
    const overlay = heroImage && Number.isFinite(heroImage.overlay) ? heroImage.overlay : 100;
    const resolvedColor = entry.color === "auto"
      ? (overlay >= 40 ? "#ffffff" : "#111111")
      : (entry.color === "black" ? "#111111" : "#ffffff");
    const styles = { color: resolvedColor };
    if (entry.shadow) styles.textShadow = "0 2px 4px rgba(0,0,0,0.8)";
    if (entry.stroke) {
      styles.WebkitTextStroke = resolvedColor === "#ffffff"
        ? "0.5px rgba(0,0,0,0.5)"
        : "0.5px rgba(255,255,255,0.5)";
    }
    return styles;
  }, [appSettings, heroImage]);

  // ----------------------------------------------------------------
  // Phase 5C — KPI stat cards row.
  // ----------------------------------------------------------------
  // Four at-a-glance metrics computed from the same data already
  // passed as ctx. Every value flows through visibleMembers so
  // role-scoped views (pastor / team_member) only surface members
  // that the current user is actually allowed to see.
  // ----------------------------------------------------------------
  const kpiStats = useMemo(() => {
    const nowMs = Date.now();
    const DAY_MS = 86400000;
    const visible = typeof visibleMembers === "function"
      ? (visibleMembers(currentUser) || [])
      : (Array.isArray(visibleMembers) ? visibleMembers : []);
    const activeMembers = visible.filter(
      (m) => !m.masked && m.memberStatus === "Active"
    ).length;
    const todayKey = ymdKey(new Date());
    const followUpsToday = (followups || []).filter((f) => {
      const nv = f.nextVisitDate;
      return typeof nv === "string" && nv.slice(0, 10) === todayKey;
    }).length;
    const tasksDue = (tasks || []).filter((t) => {
      if (t.status === "done") return false;
      if (!t.dueDate) return false;
      const d = parseDateOnly(t.dueDate);
      return d && d.getTime() <= nowMs + DAY_MS;
    }).length;
    const thirtyMs = nowMs - 30 * DAY_MS;
    const newVisitors = visible.filter((m) => {
      if (m.masked) return false;
      if (m.memberStatus !== "Visitor" && m.memberStatus !== "Guest" && m.memberStatus !== "New Member") return false;
      const jd = m.joinedDate && parseDateOnly(m.joinedDate);
      return jd && jd.getTime() >= thirtyMs;
    }).length;
    return { activeMembers, followUpsToday, tasksDue, newVisitors };
  }, [visibleMembers, followups, tasks, currentUser]);

  // Shared ctx passed to each widget render
  const ctx = {
    currentUser, members, setMembers, families: null, services, events, attendance,
    followups, users, setUsers, appointments, blockouts, tasks, setTasks, departments,
    persist, addAudit, navigate, visibleMembers,
    isSuperAdmin, isLimitedAdmin, isPastor, isTeamMember,
  };

  // Depts the user can add to their dashboard:
  //   - SA / LA / Pastor → any department
  //   - everyone else  → only depts they're a member of OR lead
  const availableDepartments = useMemo(() => {
    if (isSuperAdmin || isLimitedAdmin || isPastor) return departments;
    const uid = currentUser.id;
    return departments.filter(d => (uid && d.leaderUserId === uid) || (Array.isArray(d.users) && d.users.includes(uid)));
  }, [departments, isSuperAdmin, isLimitedAdmin, isPastor, currentUser.id]);

  return (
    <div data-testid="dashboard-page">
      {/* ==============================================================
          Phase 5C — Greeting hero
          ==============================================================
          Wraps the greeting + widget count + Customize CTA inside a
          shared <Card variant="hero">. StatusRing wraps the user avatar
          (viewer-role gated). Customize button is `<Button variant="
          tertiary">` — the ADD widgets empty-state button is the only
          primary CTA on the page.
          ============================================================== */}
      <Card
        variant="hero"
        data-testid="dashboard-hero"
        className={`dashboard-hero-card ${heroImage ? "has-hero-image" : ""}`}
      >
        {heroImage && (
          <>
            <div
              aria-hidden="true"
              data-testid="dashboard-hero-image"
              className="dashboard-hero-image"
              style={{
                backgroundImage: `url(${heroImage.image})`,
                backgroundPosition: heroImage.position,
                transform: `scale(${heroImage.zoom / 100})`,
                transformOrigin: heroImage.position,
              }}
            />
            <div
              aria-hidden="true"
              data-testid="dashboard-hero-overlay"
              className="dashboard-hero-overlay"
              style={{ background: `rgba(0,0,0,${heroImage.overlay / 100})` }}
            />
          </>
        )}
        <div
          className={`dashboard-hero-inner ${heroImage && heroImage.overlay < 20 ? "needs-text-shadow" : ""}`}
        >
          <StatusRing status="Active" viewerRole={currentUser.role}>
            <Avatar
              name={currentUser.name || "Friend"}
              photo={currentUser.photo}
              size={64}
              status="Active"
              style={{ fontSize: "var(--fs-h2)" }}
            />
          </StatusRing>
          <div className="dashboard-hero-text">
            <div className="dashboard-hero-greeting" data-testid="dashboard-hero-greeting" style={heroTextStyle}>
              {greet}, {(currentUser.name || "").split(" ")[0] || "Friend"}
            </div>
            <div className="dashboard-hero-sub" data-testid="dashboard-hero-sub" style={heroTextStyle}>
              {layout.length} widget{layout.length === 1 ? "" : "s"} on your dashboard
            </div>
          </div>
          <Button
            variant="tertiary"
            size="sm"
            data-testid="customize-dashboard-btn"
            onClick={() => setShowCustomize(true)}
            className="dashboard-hero-cta"
            style={heroTextStyle}
          >
            <CustomIcon name="tools" size={14} /> Customize
          </Button>
        </div>
      </Card>

      {/* ==============================================================
          Phase 5C — KPI stat cards row
          ==============================================================
          Four at-a-glance metrics: Active Members (green), Follow-Ups
          Today (blue), Tasks Due (amber), New Visitors 30d (purple).
          Non-admin viewers still see them — the underlying data is
          already scoped by visibleMembers.
          ============================================================== */}
      <div className="dashboard-kpi-row" data-testid="dashboard-kpi-row">
        {[
          { key: "active",     variant: "green",  icon: "people",         label: "Active members",     value: kpiStats.activeMembers, to: "/members?chip=Active" },
          { key: "followups",  variant: "blue",   icon: "chat",           label: "Follow-ups today",   value: kpiStats.followUpsToday, to: "/followuptree?feed=today" },
          { key: "tasks",      variant: "amber",  icon: "clipboard",      label: "Tasks due",          value: kpiStats.tasksDue, to: "/tasks?due=today" },
          { key: "visitors",   variant: "purple", icon: "celebrate",      label: "New visitors (30d)", value: kpiStats.newVisitors, to: "/members?chip=Visitor" },
        ].map((k) => (
          <Card
            key={k.key}
            variant={k.variant}
            compact
            data-testid={`dashboard-kpi-${k.key}`}
            className="dashboard-kpi-card is-tappable"
            role="button"
            tabIndex={0}
            onClick={() => navigate(k.to)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(k.to); } }}
          >
            <div className="dashboard-kpi-icon" aria-hidden="true">
              <CustomIcon name={k.icon} size={20} />
            </div>
            <div className="dashboard-kpi-value text-page-title">{k.value}</div>
            <div className="dashboard-kpi-label text-meta">{k.label}</div>
          </Card>
        ))}
      </div>

      {bdayNotifs.length > 0 && (
        <div
          data-testid="birthday-notif-card"
          onClick={() => {
            // Mark every unread birthday notification for the current user
            // as read, then navigate. Prevents the "2 unread notifications"
            // count from getting stuck. (Iter 82 bug fix.)
            if (typeof markNotifRead === "function") {
              bdayNotifs.forEach(n => markNotifRead(n.id));
            }
            navigate("/birthdays-this-week");
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              if (typeof markNotifRead === "function") bdayNotifs.forEach(n => markNotifRead(n.id));
              navigate("/birthdays-this-week");
            }
          }}
          style={{ background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: "var(--kc-radius-sm, 10px)", padding: "12px 18px", marginBottom: "var(--space-md)", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", transition: "background 0.15s, border-color 0.15s" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "color-mix(in srgb, var(--kc-accent) 16%, transparent)"; e.currentTarget.style.borderColor = "color-mix(in srgb, var(--kc-accent) 50%, transparent)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "color-mix(in srgb, var(--kc-accent) 10%, transparent)"; e.currentTarget.style.borderColor = "color-mix(in srgb, var(--kc-accent) 30%, transparent)"; }}
        >
          <span style={{ fontSize: 22, display: "inline-flex" }}><CustomIcon name="birthday-cake" size={22} /></span>
          <div style={{ flex: 1 }}>
            <div className="text-card-primary" style={{ fontFamily: "'Cinzel',serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>You have new birthday notifications</div>
            <div className="text-card-secondary" style={{ marginTop: 3 }}>
              {bdayNotifs.length} unread — tap to see this week&apos;s birthdays.
            </div>
          </div>
          <span style={{ color: "var(--kc-accent)", fontSize: 18, fontWeight: 700 }}>›</span>
        </div>
      )}

      {layout.length === 0 ? (
        <EmptyState
          data-testid="dashboard-empty"
          icon="sparkle"
          title="Your dashboard is empty"
          subtitle="Add widgets to see birthdays, follow-ups, events, analytics, and more — all in one place."
          action={{ label: "+ Add Widgets", onClick: () => setShowCustomize(true) }}
        />
      ) : (
        <div className="widgets-stack" data-testid="widgets-stack">
          {layout.map((id, i) => (
            <WidgetCard
              key={id}
              widgetId={id}
              index={i}
              total={layout.length}
              ctx={ctx}
              onRemove={removeWidget}
              onMove={moveByOffset}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              isDragging={dragIndex === i}
              isDropTarget={dropTargetIndex === i && dragIndex !== i}
            />
          ))}
        </div>
      )}

      {showCustomize && (
        <CustomizeDashboardModal
          role={role}
          currentLayout={layout}
          availableDepartments={availableDepartments}
          onClose={() => setShowCustomize(false)}
          onAdd={addWidget}
          onRemove={removeWidget}
          onReset={resetLayout}
        />
      )}
    </div>
  );
}


export { DashboardPage };
